import "react-international-phone/style.css";

import { InputAdornment, MenuItem, Select, TextField, Typography, type TextFieldProps } from "@mui/material";
import React from "react";
import { defaultCountries, FlagImage, parseCountry, usePhoneInput } from "react-international-phone";

// ─── Allowed country list (your 16, in your order) ───────────────────────────

const ALLOWED_ISO2 = ["in", "ae", "sa", "qa", "kw", "bh", "om", "pk", "bd", "lk", "np", "my", "sg", "gb", "us", "au"];

// Filter the library's full dataset to only your 16 countries,
// then sort them to match your desired order.
const COUNTRIES = ALLOWED_ISO2.map((iso2) => defaultCountries.find((country) => parseCountry(country).iso2 === iso2)).filter(
  (country): country is (typeof defaultCountries)[number] => Boolean(country),
);

// ─── Types ────────────────────────────────────────────────────────────────────

interface PhoneInputProps extends Omit<TextFieldProps, "onChange" | "value"> {
  /** Controlled E164 phone value e.g. "+919876543210" */
  value?: string;
  /**
   * Called on every change.
   * @param phone      E164 format  e.g. "+919876543210"
   * @param inputValue Formatted display value e.g. "+91 98765 43210"
   */
  onChange?: (phone: string, inputValue: string) => void;
  /** ISO2 country code for default country. Defaults to 'in' (India). */
  defaultCountry?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

// ─── Focus color — matches InputField exactly ────────────────────────────────

const FOCUS_COLOR = "#674BF5";

// ─── Component ────────────────────────────────────────────────────────────────

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value = "",
  onChange,
  defaultCountry = "in",
  onBlur,
  onFocus,
  label = "Phone Number",
  placeholder = "Phone number",
  error,
  helperText,
  disabled,
  required = false,
  size = "small",
  fullWidth,
  name,
  sx,
  ...rest
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry,
    value,
    countries: COUNTRIES,
    forceDialCode: true,
    onChange: (data) => {
      const isJustDialCode = COUNTRIES.some((c) => data.phone === `+${parseCountry(c).dialCode}`);
      const phone = data.phone && !isJustDialCode ? data.phone : "";
      onChange?.(phone, data.inputValue);
    },
  });

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  return (
    <TextField
      label={label}
      name={name}
      size={size}
      disabled={disabled}
      required={required}
      fullWidth={fullWidth}
      // inputValue is the formatted display string (e.g. "+91 98765 43210")
      value={inputValue}
      onChange={handlePhoneValueChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      error={error && !isFocused}
      helperText={!isFocused ? helperText : " "}
      type="tel"
      placeholder={placeholder}

      inputRef={inputRef}
      autoComplete="off"
      slotProps={{
        inputLabel: {
          sx: { "&.Mui-focused": { color: FOCUS_COLOR } },
        },
        input: {
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: "2px", ml: "-8px" }}>
              {/*
               * MUI Select renders the flag trigger.
               * Its outline is hidden so it blends into the TextField border —
               * the fieldset is only shown when the Select itself is focused
               * (i.e. keyboard navigation), not when the text input is focused.
               */}
              <Select
                MenuProps={{
                  // Constrain dropdown height; align with left edge
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      width: 280,
                      mt: "4px",
                      borderRadius: "6px",
                      border: (t) => `1px solid ${t.palette.divider}`,
                      boxShadow: 6,
                      "& .MuiMenuItem-root": {
                        gap: 1,
                        fontSize: "0.85rem",
                        py: "7px",
                      },
                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: `${FOCUS_COLOR}14`,
                        "&:hover": { backgroundColor: `${FOCUS_COLOR}20` },
                      },
                    },
                  },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                }}
                sx={{
                  // Width hugs the flag icon only
                  width: "max-content",
                  // Hide the outlined border — it would double-up with TextField's border
                  fieldset: { display: "none" },
                  // Show it only when the Select button itself has focus
                  // (tab navigation), not when the text input is focused
                  '&.Mui-focused:has(div[aria-expanded="false"])': {
                    fieldset: { display: "block", borderColor: FOCUS_COLOR },
                  },
                  // Tighten the internal padding
                  ".MuiSelect-select": { p: "8px", pr: "24px !important" },
                  // Pull chevron tight to the right edge
                  svg: { right: 0 },
                }}
                value={country.iso2}
                onChange={(e) => setCountry(e.target.value as string, { focusOnInput: true })}
                // Trigger shows only the flag emoji
                renderValue={(iso2) => <FlagImage iso2={iso2 as string} style={{ display: "flex", width: 24, height: 16 }} />}
              >
                {COUNTRIES.map((c) => {
                  const { name, iso2, dialCode } = parseCountry(c);
                  return (
                    <MenuItem key={iso2} value={iso2}>
                      <FlagImage iso2={iso2} style={{ width: 24, height: 16, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        {name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: iso2 === country.iso2 ? FOCUS_COLOR : "text.disabled",
                          fontWeight: 600,
                          minWidth: 36,
                          textAlign: "right",
                        }}
                      >
                        +{dialCode}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
        },
        formHelperText: {
          sx: {
            minHeight: "20px", // prevents layout jump on error
            margin: 0,
          },
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused fieldset": { borderColor: FOCUS_COLOR },
        },
        "& .MuiInputLabel-root.Mui-focused": { color: FOCUS_COLOR },
        ...sx,
      }}
      {...rest}
    />
  );
};
